/**
 * The Overview — and the one rule that makes it a tool rather than a decoration.
 *
 * **Every figure is a link to the list that produced it.** The operator's words: *"All clickable to
 * filter exactly what was stated."* Pressing "4 defaulters" opens Sales filtered to defaulters — not
 * Sales unfiltered, and not a different screen that happens to mention them. A dashboard whose
 * numbers cannot be opened makes somebody go and find the same rows by hand, which is worse than not
 * showing the number at all. Item 12 of `14-DEVELOPMENT-MODULE-PLAN.md`.
 *
 * **The figures and the lists are the same arithmetic, not two copies of it.** Every count on this
 * screen is `filterSales(...).length` over the same rows the Sales list draws, filtered by the same
 * `SalesFilter` the link carries — see `lib/sales.ts`. So a card reading 3 cannot open a list of
 * eleven, because the card *is* the list, counted. That is the failure this rule exists to prevent
 * and the only construction that rules it out rather than testing for it afterwards.
 *
 * **It spans every Property**, on the operator's decision — picking one is what the Properties list
 * is for.
 *
 * Two figures the specification asks for are not drawn, and are named here rather than quietly
 * dropped: **"collected this period against what was due"** and **the value still unsold**. Neither
 * has a source. `GET /developer/summary` reports collections and arrears for all time and not by
 * period, and `GET /estates` carries counts but no asking prices, so a value of unsold stock would
 * have to be assembled from one tree request per Property. Both are recorded in the handoff as API
 * work.
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { soldNoun } from "@rentorng/core";
import { useSession } from "@/lib/session";
import { useModuleLink } from "@/lib/module-rail";
import { api } from "@/lib/api";
import { useFormat, useToday } from "@/lib/format";
import { filterSales, salesLink, type SalesFilter } from "@/lib/sales";
import { useSales } from "./DevelopmentSales";
import {
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  LinkButton,
  PageHeader,
  Spinner,
  StatTile,
  Table,
} from "@/components/ui";

interface Summary {
  counts: { estates: number; plots: number; available: number; buyers: number; active: number; unallocated: number };
  money: { soldLabel: string; collectedLabel: string; arrearsLabel: string; arrears: number };
  behind: Array<{
    purchaseId: string;
    reference: string;
    buyerName: string;
    buyerPhone: string | null;
    estateName: string;
    arrears: number;
    arrearsLabel: string;
    oldest: string;
  }>;
}

interface EstateRow {
  id: string;
  name: string;
  sells: string;
  status: string;
  plots: number;
  available: number;
  sold: number;
}

interface PhaseRow {
  id: string;
  name: string;
  status: string;
  nodes: number;
  available: number;
}

/**
 * How many Properties this screen will ask about phases for.
 *
 * One request per Property is the only way to get phase counts today — `GET /estates/:id/phases`
 * carries them and nothing rolls them up — and twenty parallel requests is the point at which that
 * stops being a reasonable trade. The section says so when it is capped rather than silently
 * reporting a subset, because a phase that has stalled being invisible is the exact failure this
 * section exists to prevent.
 */
const PHASE_FANOUT = 20;

/**
 * A figure, and the list behind it.
 *
 * `to` is not optional. A card on this screen without a destination would be the thing item 12
 * forbids, and making the type refuse it is cheaper than remembering.
 */
function Figure({
  label,
  value,
  sub,
  to,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  to: string;
  tone?: "danger" | "warn" | "success" | "muted" | "brand";
}) {
  return <StatTile label={label} value={value} sub={sub} to={to} tone={tone ?? "muted"} />;
}

export function DeveloperOverview() {
  const fmt = useFormat();
  const today = useToday();
  const { modules } = useSession();
  const link = useModuleLink(modules);

  const summary = useQuery({
    queryKey: ["developer-summary"],
    queryFn: () => api.get<Summary>("/developer/summary"),
  });

  const properties = useQuery({
    queryKey: ["estates", "overview"],
    queryFn: () => api.get<{ estates: EstateRow[] }>("/estates?perPage=200&sort=name"),
  });

  const sales = useSales();

  const estates = properties.data?.estates ?? [];

  /*
    Phase counts, one request per Property.

    `useQueries` rather than a loop of `useQuery`, because the number of them changes as the
    Properties land and a variable number of hooks is a crash rather than a bug. Each is allowed to
    fail on its own: a Property the caller may not open should cost this screen a missing line, not
    an error state over everything else.
  */
  const phaseQueries = useQueries({
    queries: estates.slice(0, PHASE_FANOUT).map((estate) => ({
      queryKey: ["estate", estate.id, "phases"],
      queryFn: () => api.get<{ phases: PhaseRow[] }>(`/estates/${estate.id}/phases`),
      staleTime: 60_000,
      retry: false,
    })),
  });

  const phaseLines = useMemo(
    () =>
      estates.slice(0, PHASE_FANOUT).flatMap((estate, index) =>
        (phaseQueries[index]?.data?.phases ?? []).map((phase) => ({ estate, phase })),
      ),
    [estates, phaseQueries.map((query) => query.data).join("|")],
  );

  /** One count, and the address that shows exactly those rows. */
  const count = (filter: SalesFilter) => filterSales(sales.rows, filter, today).length;

  if (summary.isLoading || properties.isLoading || sales.isLoading) return <Spinner />;
  if (summary.error || properties.error || sales.error) {
    return (
      <ErrorState
        error={summary.error ?? properties.error ?? sales.error}
        retry={() => {
          void summary.refetch();
          void properties.refetch();
          sales.refetch();
        }}
      />
    );
  }

  const money = summary.data?.money;
  const behind = summary.data?.behind ?? [];

  /*
    What is left to sell, across the whole book.

    **Taken from `GET /developer/summary` rather than by adding up the Properties list**, and the
    reason is a live bug rather than a preference: the per-scheme counts on `GET /estates` are all
    zero. Its correlated sub-selects reference the outer column as a bare `"id"`, which Drizzle emits
    unqualified for a single-table select, and inside `(select … from "plots" p where p."estate_id" =
    "id")` that `"id"` binds to the *plot's* own id — so the predicate is never true. The summary
    endpoint writes its SQL out longhand and is therefore correct. Both are recorded in
    `.context/00-HANDOFF.md`; this screen leads with the figures that are right.

    Reserved is the one the stock counts cannot answer whatever happens, because a hold lives on the
    reservation and not on the plot's own row — so it comes from the sales, where the chips get it.
  */
  const forSale = summary.data?.counts.plots ?? 0;
  const available = summary.data?.counts.available ?? 0;
  const sold = count({ sold: true });
  const reserved = count({ stage: "reserved" });

  const needsDoing = [
    {
      label: "Instalments overdue",
      value: count({ overdue: true }),
      to: salesLink({ overdue: true }),
      hint: "A payment on the plan has passed its due date.",
      tone: "danger" as const,
    },
    {
      label: "Awaiting allocation",
      value: count({ unallocated: true }),
      to: salesLink({ unallocated: true }),
      hint: "Sold, with no particular unit assigned yet.",
      tone: "warn" as const,
    },
    {
      label: "Awaiting handover",
      value: count({ awaitingHandover: true }),
      to: salesLink({ awaitingHandover: true }),
      hint: "Settled in full and not yet delivered.",
      tone: "warn" as const,
    },
    {
      label: "Holds running out",
      value: count({ lapsing: true }),
      to: salesLink({ lapsing: true }),
      hint: "A reservation that has expired, or expires within the week.",
      tone: "warn" as const,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle={`${estates.length} ${estates.length === 1 ? "property" : "properties"} · ${forSale} for sale`}
        description="What is left to sell across every property, what is owed, and what needs doing. Every figure opens the list behind it."
        actions={
          <LinkButton to="/app/development/properties/new" need="property.edit">
            Add a property
          </LinkButton>
        }
      />

      {/* ------------------------------------------------- what is left to sell */}

      <h2 className="mb-3 text-sm font-semibold text-ink-700">What is left to sell</h2>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="For sale" value={String(forSale)} sub="Across every property" to="/app/development/properties" />
        <Figure
          label="Available"
          value={String(available)}
          sub="Nothing held against them"
          to="/app/development/properties"
          tone="success"
        />
        <Figure label="Reserved" value={String(reserved)} sub="Held, not yet converted" to={salesLink({ stage: "reserved" })} tone="warn" />
        <Figure label="Sold" value={String(sold)} sub="Every sale on record" to={salesLink({ sold: true })} tone="brand" />
      </div>

      {/* ------------------------------------------------------- what is owed */}

      <h2 className="mb-3 text-sm font-semibold text-ink-700">What is owed</h2>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="Value sold" value={money?.soldLabel ?? "—"} sub="Agreed prices, every sale" to={salesLink({ sold: true })} />
        {/* `withModule` rather than a hand-written `?module=developing`: a development-only agency
            has nothing to disambiguate, and an address carrying a redundant parameter is one
            somebody copies into a bug report. */}
        <Figure label="Received" value={money?.collectedLabel ?? "—"} sub="Every receipt" to={link("/app/payments")} />
        <Figure
          label="In arrears"
          value={money?.arrearsLabel ?? "—"}
          sub={`${behind.length} ${behind.length === 1 ? "buyer" : "buyers"} behind`}
          to={salesLink({ overdue: true })}
          tone={(money?.arrears ?? 0) > 0 ? "danger" : "muted"}
        />
        <Figure
          label="Defaulters"
          value={String(count({ stage: "defaulted" }))}
          sub="Awaiting a decision"
          to={salesLink({ stage: "defaulted" })}
          tone={count({ stage: "defaulted" }) > 0 ? "danger" : "muted"}
        />
      </div>

      {/* ---------------------------------------------------- what needs doing */}

      <h2 className="mb-3 text-sm font-semibold text-ink-700">What needs doing</h2>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {needsDoing.map((item) => (
          <Figure
            key={item.label}
            label={item.label}
            value={String(item.value)}
            sub={item.hint}
            to={item.to}
            tone={item.value > 0 ? item.tone : "muted"}
          />
        ))}
      </div>

      {/* ------------------------------------------------------ per property */}

      <Card padded={false} className="mb-6">
        <div className="border-b border-ink-200 p-4">
          <CardTitle hint="Which scheme is moving and which is not.">By property</CardTitle>
        </div>
        {!estates.length ? (
          <EmptyState
            title="No properties recorded."
            hint="A property is the scheme being sold. Add the property first, then its contents — a numbered range can be created in one step."
            action={
              <LinkButton to="/app/development/properties/new" need="property.edit">
                Add a property
              </LinkButton>
            }
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Property</th>
                <th className="num">For sale</th>
                <th className="num">Available</th>
                <th className="num">Reserved</th>
                <th className="num">Sold</th>
              </tr>
            }
          >
            {estates.map((estate) => {
              const noun = soldNoun(estate.sells);
              return (
                <tr key={estate.id}>
                  <td>
                    <Link
                      className="font-medium text-brand-700 hover:underline"
                      to={`/app/development/properties/${estate.id}`}
                    >
                      {estate.name}
                    </Link>
                    <div className="text-xs text-ink-400">{noun.many}</div>
                  </td>
                  <td className="num">{estate.plots}</td>
                  <td className="num">{estate.available}</td>
                  <td className="num">
                    {/* Straight to that scheme's held units, not to every reservation. */}
                    <Link className="text-brand-700 hover:underline" to={salesLink({ stage: "reserved", estateId: estate.id })}>
                      {count({ stage: "reserved", estateId: estate.id })}
                    </Link>
                  </td>
                  <td className="num">
                    {/* Counted from the sales rather than from `estate.sold`, which is one of the
                        figures the correlated-subquery bug in `GET /estates` returns as zero — and
                        counted the same way the link filters, so the figure and its list agree. */}
                    <Link className="text-brand-700 hover:underline" to={salesLink({ sold: true, estateId: estate.id })}>
                      {count({ sold: true, estateId: estate.id })}
                    </Link>
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      {/* ---------------------------------------------------------- by phase */}

      {/*
        Asked for by name — *"phase item counts"*.

        The same figures the phase chips carry on a Property, gathered here, so a phase that has
        stalled is visible without opening the scheme it belongs to. Drawn only where a phase exists:
        most schemes have none, and a heading over an empty table is the module getting heavy again.
      */}
      {phaseLines.length ? (
        <Card padded={false} className="mb-6">
          <div className="border-b border-ink-200 p-4">
            <CardTitle hint="Every release, and how much of it is left.">By phase</CardTitle>
          </div>
          <Table
            head={
              <tr>
                <th>Phase</th>
                <th>Property</th>
                <th className="num">In the release</th>
                <th className="num">Available</th>
              </tr>
            }
          >
            {phaseLines.map(({ estate, phase }) => (
              <tr key={phase.id}>
                <td>
                  <Link
                    className="font-medium text-brand-700 hover:underline"
                    to={`/app/development/properties/${estate.id}?phase=${phase.id}`}
                  >
                    {phase.name}
                  </Link>
                </td>
                <td className="text-ink-600">{estate.name}</td>
                <td className="num">{phase.nodes}</td>
                <td className="num">{phase.available}</td>
              </tr>
            ))}
          </Table>
          {estates.length > PHASE_FANOUT ? (
            <p className="border-t border-ink-200 px-4 py-3 text-xs text-ink-500">
              Phases are shown for the first {PHASE_FANOUT} properties. Open a property to see the rest.
            </p>
          ) : null}
        </Card>
      ) : null}

      {/* -------------------------------------------------------- defaulters */}

      <Card padded={false}>
        <div className="border-b border-ink-200 p-4">
          <CardTitle
            hint="Buyers with an overdue balance, largest first."
            action={
              <Link className="text-sm font-medium text-brand-700 hover:underline" to={salesLink({ overdue: true })}>
                Open in Sales
              </Link>
            }
          >
            Buyers in arrears
          </CardTitle>
        </div>
        {!behind.length ? (
          <EmptyState title="No buyers in arrears." hint="Every payment plan is up to date." />
        ) : (
          <Table
            head={
              <tr>
                <th>Buyer</th>
                <th>Property</th>
                <th>Overdue since</th>
                <th className="num">Amount overdue</th>
              </tr>
            }
          >
            {behind.map((row) => (
              <tr key={row.purchaseId}>
                <td>
                  <Link
                    className="font-medium text-brand-700 hover:underline"
                    to={`/app/development/sales/${row.purchaseId}`}
                  >
                    {row.buyerName}
                  </Link>
                  <div className="text-xs text-ink-500">{row.reference}</div>
                </td>
                <td className="text-ink-600">{row.estateName}</td>
                <td className="text-ink-600">{fmt.date(row.oldest)}</td>
                <td className="num font-semibold text-danger-600">{row.arrearsLabel}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
